import React, { ForwardedRef, forwardRef, memo } from "react";
import { styled } from '@mui/material/styles';
import Stack from "@mui/material/Stack";
import { active, EDButton, EDTypography, error, grey, spacing, success } from "@trailblazer-game/ed-webkit";
import Box from "@mui/material/Box";
import { theme } from "../../../Theme/Theme";
import { getTimestampDateFormattedNoYearNoSeconds } from "../../../../helpers/dates";
import { useRouter } from "next/router";
import { EPage } from "../../../../helpers/constants";
import { ILeagueRank, ILeagueWeek } from "../../../../helpers/interfaces/league";
import useMediaQuery from "@mui/material/useMediaQuery";
import Image from "next/image";
import { IWithLazyLoadProps, withLazyLoad } from "../../../../hooks/withLazyLoad";
import { ELeaguePhase } from "../Public/League_State";

interface ILeagueHeaderProps extends IWithLazyLoadProps {
    className?: string;
    name: string;
    shortName: string;
    bgImageUrl: string;
    startTimestamp: number;
    endTimestamp: number;
    totalMatchesLabel: string;
    ranks: ILeagueRank[];
    phase: ELeaguePhase;
    totalMatchesImg: string;
}

const LeagueHeader = memo(forwardRef(({
                                          className,
                                          name,
                                          shortName,
                                          bgImageUrl,
                                          startTimestamp,
                                          endTimestamp,
                                          totalMatchesLabel,
                                          ranks,
                                          phase,
                                          totalMatchesImg
                                      }: ILeagueHeaderProps, ref: ForwardedRef<HTMLDivElement>) => {
    const router = useRouter();
    const isMedium = useMediaQuery(theme.breakpoints.down('md'));

    if (!name) {
        return null;
    }

    function getDateRange() {
        if (startTimestamp === undefined || endTimestamp === undefined) {
            return '';
        }

        return `${getTimestampDateFormattedNoYearNoSeconds(startTimestamp)} - ${getTimestampDateFormattedNoYearNoSeconds(endTimestamp)}`
    }

    function onPlayClick(e: React.MouseEvent<HTMLButtonElement>) {
        router.push(`/${EPage.GAME}`);
    }

    const stillHasToStart = phase == ELeaguePhase.StartingSoon;
    const ended = phase == ELeaguePhase.Ended;

    let totalMatches = 0;

    if (ranks) {
        ranks?.forEach((r) => totalMatches += (r.plays || 0));
    }

    return (
        <Stack className={className} ref={ref}>
            <Stack spacing={1} alignItems={"center"} justifyContent={"center"} className={"content"}>
                <Stack direction={isMedium ? "column" : "row"} spacing={1} alignItems={"center"}
                       justifyContent={"center"}>
                    <EDTypography variant={"caption"} sx={{ color: grey("200") }}>{getDateRange()}</EDTypography>
                    <Stack className={`status ${!stillHasToStart && !ended ? "on-going" : ""}`} alignItems={"center"}
                           justifyContent={"center"} direction={"row"}
                           spacing={1}>
                        <Box className={`dot ${ended || stillHasToStart ? 'red' : ''}`}/>
                        <EDTypography
                            variant={"caption"}>{stillHasToStart ? "Starting soon" : ended ? "Ended" : "Ongoing"}</EDTypography>
                    </Stack>
                </Stack>

                <EDTypography variant={"h1"} className={"league-name"} align={"center"}
                              title={name}>
                    {shortName}
                </EDTypography>

                <Stack className={"league-stats"} direction={"row"} justifyContent={"center"} spacing={4}>
                    <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
                        <Box sx={{ width: "42px", height: "42px", position: "relative" }}>
                            <Image src={`${totalMatchesImg}.webp`} alt={"Avatar"} layout={"fill"}/>
                        </Box>
                        <Stack direction={"column"}>
                            <EDTypography variant={"h4"} sx={{ color: active("primary") }}>
                                {ranks?.length}
                            </EDTypography>
                            <EDTypography variant={"caption"} sx={{ color: grey("500") }}>
                                {totalMatchesLabel}
                            </EDTypography>
                        </Stack>
                    </Stack>

                    <Stack direction={"row"} alignItems={"center"} justifyContent={"center"} spacing={1}>
                        <Box sx={{ width: "42px", height: "42px", position: "relative" }}>
                            <Image src={"/league/icon_match.webp"} alt={"Avatar"} layout={"fill"}/>
                        </Box>
                        <Stack direction={"column"}>
                            <EDTypography variant={"h4"} sx={{ color: active("primary") }}>
                                {Math.ceil(totalMatches / 2)}
                            </EDTypography>
                            <EDTypography variant={"caption"} sx={{ color: grey("500") }}>
                                Matches
                            </EDTypography>
                        </Stack>
                    </Stack>
                </Stack>

                <EDButton label={"Play"} onClick={onPlayClick}></EDButton>
            </Stack>
            <Box className={"background"} sx={{backgroundImage: `url(${bgImageUrl}.webp)`}}>
                <Box className={"overlay"}/>
            </Box>
        </Stack>
    );
}));

LeagueHeader.displayName = "LeagueHeader";

const LeagueHeaderStyled = styled(LeagueHeader)((props: any) => ({
    zIndex: 10,
    width: "100%",
    "> .content": {
        position: "relative",
        zIndex: 1,
        height: "100%",
        padding: theme.spacing(8, 0),
    },
    ".background": {
        position: "absolute",
        width: "100%",
        aspectRatio: '3',
        margin: "0 !important",
        top: `0`,
        left: `-50%`,
        maxWidth: "1920px",
        transform: "translate(50%, 0)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        ".overlay": {
            position: "absolute",
            top: 0,
            left: `50%`,
            transform: "translate(-50%, 0)",
            background: "linear-gradient(90deg, rgba(33, 33, 33, 0) 0%, rgba(33, 33, 33, 0.7) 30%, rgba(33, 33, 33, 0.7) 70%, rgba(33, 33, 33, 0) 100%);",
            height: "100%",
            width: "100%",
            maxWidth: "1440px",
            zIndex: 5
        }
    },
    ".status": {
        backgroundColor: grey("900"),
        border: `2px solid ${grey("1000")}`,
        padding: theme.spacing(0.5, 1),
        borderRadius: '4px',
        color: grey("400"),
        zIndex: 10,
        "&.on-going": {
            backgroundColor: success("main"),
            color: grey("900"),
            ".dot": {
                backgroundColor: grey("900")
            }
        },
        ".dot": {
            width: '8px',
            height: '8px',
            borderRadius: '8px',
            backgroundColor: success("main"),
            "&.red": {
                backgroundColor: error("main")
            }
        }
    },
    ".league-name": {
        color: "transparent",
        background: theme.gradients.primary,
        backgroundClip: "text",
        webkitTextFillColor: "transparent",
        textShadow: "0px 2px 16px rgba(0, 0, 0, 0.1)",
        fontWeight: "bold"
    },
    ".league-stats": {
        backgroundColor: `rgba(0, 0, 0, 0.25)`,
        padding: spacing(2)
    },
    [theme.breakpoints.down("md")]: {
        paddingTop: 'calc(75vw/1.14)',
        ".background": {
            backgroundImage: `url(${props.bgImageUrl}-mobile.webp)`,
            aspectRatio: "1.14",
            "&:before": {
                content: '" "',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '25%',
                background: `linear-gradient(180deg, rgba(33,33,33,0) 0%, ${grey("900")} 75%)`
            },
            ".overlay": {
                display: "none"
            }
        }
    }
}));

// Export
export default withLazyLoad(LeagueHeaderStyled);
